import { ICurrentUser } from '@dadohr/shared/domainTypes/currentUser';
import { mapWithConcurrencySettled } from '../concurrency';
import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  Audience,
  AutomationAction,
  AutomationActionStatus,
  AutomationActionTemplate,
  AutomationStatus,
  BatchMode,
  ColumnBlock,
  ContentType,
  EnrollSource,
  ExperienceActorType,
  ExperienceAnchor,
  ExperienceAnchorType,
  ExperienceChannelActor,
  ExperienceEventType,
  ExperienceStatus,
  ExperienceTemplateMessage,
  ExperienceTemplateStatus,
  ExperienceUserActor,
  ExternalServiceType,
  getSubTaskTemplates,
  IAutomation,
  IAutomationTemplate,
  IExperience,
  IExperienceEvent,
  IExperienceTemplate,
  IExperienceTemplateResource,
  IExperienceTemplateResourceExcel,
  IExperienceTemplateResourceMicrosoftOneDriveFolder,
  IExperienceTemplateResourceMicrosoftSharepointFolder,
  IIgnoredMissingActor,
  IItemTemplateTiming,
  IItemTrigger,
  IItemTriggerResolved,
  isEventDrivenTiming,
  InternalStorageInstructionType,
  IReminderTemplate,
  isLayoutTemplateBlock,
  isSubTaskTemplateBlock,
  ISubTask,
  ISupportingActor,
  ITask,
  ITaskTemplate,
  ITimelineItem,
  LayoutBlock,
  LayoutBlockType,
  MessageStatus,
  PARTICIPANT_ACTOR_KEY,
  SubmitTargetType,
  SubTaskHookType,
  SubTaskStatus,
  SubTaskTemplate,
  SupportingActorType,
  TaskStatus,
  TemplateResourceType,
  UserField
} from '@dadohr/shared/domainTypes/experience';
import { Rule } from '@dadohr/shared/domainTypes/rules';
import {
  CUSTOMER_ACTIONS,
  ManualEnrollmentAction
} from '@dadohr/shared/domainTypes/internalAlerts';
import {
  ACTOR_OVERRIDE_KEY_PREFIX,
  ANCHOR_OVERRIDE_KEY
} from '@dadohr/shared/domainTypes/instanceFieldOverride';
import { ScheduleChannelType } from '@dadohr/shared/domainTypes/schedules';
import { Timestamp } from '@dadohr/shared/domainTypes/time';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import assertNever from 'assert-never';
import {
  cloneDeep,
  compact,
  flatten,
  fromPairs,
  get,
  keyBy,
  mapValues,
  orderBy,
  partition,
  startCase
} from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import shortid from 'shortid';
import { AnchorField } from '../../features/Experiences/components/Anchors';
import { callFirebaseFunction } from '../firebaseFunctions';
import { getSelectableSupportingActors } from './actorUsage';
import { derivePreSkipsFromStagedInstance } from './derivePreSkipsFromStagedInstance';
import { deriveTimingOverridesFromStagedInstance } from './deriveTimingOverridesFromStagedInstance';
import {
  batchSetDocs,
  combineLoadingValues,
  getAllByIdAsMap,
  LoadingValue,
  refreshTimestamp,
  removeDoc,
  setDoc,
  store,
  useMappedLoadingValue
} from '../firebase';
import {
  CollectionListener,
  createCollectionListenerStore,
  useCollectionListener
} from '../firecache/collectionListener';
import {
  createDocumentListenerGetter,
  useDocumentListener,
  useDocumentListeners
} from '../firecache/documentListener';
import { tryReportCustomerAction } from '../internalAlerts';
import { traverse } from '../object';
import { hasCurrentUserExperienceLevelScopes } from '../permissions';
import {
  analyzeExperienceInterpolation,
  analyzeExperienceInterpolationsInBlocks,
  getUserFieldForAnchor,
  mergeInterpolationAnalysisResults
} from '../set/interpolation';
import {
  addToTs,
  compareTimestamps,
  now,
  ONE_DAY,
  tsFromMillis,
  tsIsAfter
} from '../time';
import { matchUser, useUsersByOrgId } from '../user';
import {
  filterBlocksForAudience,
  getAudienceForUser,
  getEffectiveAudiences,
  getEveryoneAudience
} from './audience';
import { toExperienceTemplateDoc } from './documents';
import {
  getDuplicateEnrollmentForm,
  getEnrollmentFormByTemplate
} from './enrollmentForms';
import {
  generateAccessToken,
  getParticipantActor,
  isExperienceUserActor,
  useExperienceInstancesCurrentlyRelevant
} from './instance';
import {
  getAnchorOffsetForExperience,
  getFixedOrConvertToMessageFixedTimingConsideringTriggerChain,
  getFixedOrConvertToTaskFixedTimingConsideringTriggerChain,
  getFixedTimingOrApproximateParentTiming,
  getFreshNonTaskStartDate,
  getFreshTaskDates
} from './timing';

const experienceTemplateCollection = () =>
  store().collection(FS.experienceTemplates);

const getExperienceTemplatesListenerByOrgId = createCollectionListenerStore(
  (orgId) =>
    new CollectionListener(
      experienceTemplateCollection().where('orgId', '==', orgId),
      toExperienceTemplateDoc
    )
);

const getExperienceTemplateListenerById = createDocumentListenerGetter(
  (id) => experienceTemplateCollection().doc(id),
  toExperienceTemplateDoc
);

export const useExperienceTemplatesByOrgId = (orgId: string) => {
  return useCollectionListener(getExperienceTemplatesListenerByOrgId(orgId));
};

export const useReportableExperienceTemplatesByOrgId = (orgId: string) => {
  return useMappedLoadingValue(
    useCollectionListener(getExperienceTemplatesListenerByOrgId(orgId)),
    (tpls) =>
      tpls.filter(
        (tpl) =>
          tpl.data.status !== ExperienceTemplateStatus.ARCHIVED &&
          tpl.data.status !== ExperienceTemplateStatus.DRAFT &&
          tpl.data.status !== ExperienceTemplateStatus.ROUGH_DRAFT
      )
  );
};

export const getExperienceTemplatesByOrgId = (orgId: string) => {
  return getExperienceTemplatesListenerByOrgId(orgId).get();
};

export const useActiveExperienceTemplatesByOrgId = (orgId: string) => {
  return useMappedLoadingValue(useExperienceTemplatesByOrgId(orgId), (tpls) =>
    tpls.filter((tpl) => tpl.data.status === ExperienceTemplateStatus.ACTIVE)
  );
};

export const useActiveExperienceTemplatesCurrentUserCanSee = (
  currentUser: ICurrentUser
) => {
  const mapFn = useCallback(
    (tpls: Doc<IExperienceTemplate>[]): Doc<IExperienceTemplate>[] => {
      return tpls.filter((tpl) =>
        hasCurrentUserExperienceLevelScopes(currentUser, tpl.id, ['view'])
      );
    },
    [currentUser]
  );
  return useMappedLoadingValue(
    useActiveExperienceTemplatesByOrgId(currentUser.org.id),
    mapFn,
    true
  );
};

// Grace period after an instance's expected end before it auto-archives.
// Older templates (and field-masked minimal reads) may lack the field.
export const getTemplateExpectedEndThreshold = (tpl: {
  expectedEndThreshold?: number;
}): number => tpl.expectedEndThreshold ?? ONE_DAY * 14;

export const useExperienceTemplateById = (tplId: string) => {
  return useDocumentListener(getExperienceTemplateListenerById(tplId));
};

export const getExperienceTemplateById = (tplId: string) => {
  return getExperienceTemplateListenerById(tplId).get();
};

export const getEmptyExperienceDraft = (
  orgId: string,
  userId: string,
  email: string,
  tz: string,
  startingHour: number,
  enableSso: boolean
) => {
  const id = shortid();
  const createdAt = now();
  const blockId = shortid();
  const msg: ExperienceTemplateMessage = {
    id: shortid(),
    batchMode: BatchMode.DEFAULT,
    actor: PARTICIPANT_ACTOR_KEY,
    subject: '[{{ experience.title }}] A new experience starts today!',
    taskList: { range: 'ALL', variant: 'expanded' },
    body: {
      t: '',
      v: 1
    },
    blocks: [
      {
        id: blockId,
        type: ContentType.TEXT,
        parentLayoutElement: null,
        data: {
          t: {
            t: '',
            v: 1
          }
        }
      }
    ],
    audiences: [
      {
        id: shortid(),
        name: '',
        color: '',
        rule: 'EVERYONE',
        blocks: [blockId]
      }
    ],
    channels: [{ type: ScheduleChannelType.EMAIL, emailFrom: 'email' }],
    sendAt: 0,
    startTime: null,
    timing: {
      type: 'fixed',
      start: 0,
      time: null
    },
    preferUserTimezone: false,
    catchup: { disabled: false }
  };
  const doc: Doc<IExperienceTemplate> = {
    id,
    collection: FS.experienceTemplates,
    data: {
      id,
      orgId,
      tz,
      preferUserTimezone: false,
      title: 'Unnamed Experience',
      adminTitle: '',
      description: {
        t: '',
        v: 1
      },
      messages: {
        intro: null, // this will be removed once we're ready - just there to satisfy the current typing
        other: [msg],
        outro: null
      },
      supportingActors: {},
      enrollFuture: true,
      enrollNewUsers: false,
      enrollModifiedUsers: false,
      enrollRule: null, //{ type: 'all', els: [] },
      anchor: {
        type: ExperienceAnchorType.IMMEDIATE,
        offset: {
          type: 'ABSOLUTE',
          duration: 0,
          hour: startingHour
        },
        autoEnrollmentRejectionThreshold: null,
        autoEnrollmentRejectionEnabled: false,
        v: 2
      },
      tasks: [],
      automations: [],

      createdBy: userId,
      createdAt: createdAt,
      updatedBy: userId,
      updatedAt: createdAt,

      status: ExperienceTemplateStatus.DRAFT,
      contacts: [
        {
          type: 'MAIL',
          name: '',
          email
        }
      ],
      notificationSender: {
        name: '',
        replyTo: email
      },
      resources: [],
      sso: enableSso ? { type: 'AFTER_FIRST_DAY_AT_WORK', offset: 0 } : null,
      v: 1,
      expectedEndThreshold: 14 * ONE_DAY,

      employeeApp: {
        banner: { url: '' },
        contacts: [],
        resources: [],
        instancePageNavStylePerActor: {},
        v: 1
      },

      previewToken: { token: generateAccessToken(), active: false }
    }
  };
  return doc;
};

export const duplicateMessageTemplate = (
  m: ExperienceTemplateMessage
): ExperienceTemplateMessage => ({
  ...cloneDeep(m),
  id: shortid(),
  timing: cloneDeep(m.timing)
});

export const duplicateAutomationTemplate = (
  _d: IAutomationTemplate
): IAutomationTemplate => {
  const d = cloneDeep(_d);
  const actionIdMap: { [oldId: string]: string } = {};
  d.actions.forEach((a) => (actionIdMap[a.id] = shortid()));
  return {
    ...d,
    id: shortid(),
    actions: d.actions.map<AutomationActionTemplate>((a) => {
      return {
        ...a,
        id: actionIdMap[a.id]
      };
    }),
    audiences: d.audiences.map<Audience>((a) => {
      return {
        ...a,
        blocks: compact(a.blocks.map((b) => actionIdMap[b]))
      };
    }),
    timing: cloneDeep(d.timing)
  };
};

export const duplicateTaskTemplate = (
  _t: ITaskTemplate,
  resourceIdToNewId: {
    [oldId: string]: string;
  }
): ITaskTemplate => {
  console.log(_t);
  const t = cloneDeep(_t);
  const blockIdsToNewId: { [key: string]: string } = {};
  // TODO remove
  // t.subTasks.forEach((s) => (blockIdsToNewId[s.id] = shortid()));
  t.blocks.forEach((s) => (blockIdsToNewId[s.id] = shortid()));
  const customCompletionCriteria = t.customCompletionCriteria.map((c) => {
    if (c.type === 'SUBSET') {
      return {
        ...c
      };
    }
    return assertNever(c.type);
  });

  const duplicateLayoutTemplate = (b: LayoutBlock) => {
    if (b.type === LayoutBlockType.COLUMNS) {
      const nextColumnBlock: ColumnBlock = {
        ...b,
        data: {
          ...b.data,
          columns: b.data.columns.map((c) => ({
            ...c,
            blockId: blockIdsToNewId[c.blockId]
          }))
        }
      };
      return nextColumnBlock;
    }
    return assertNever(b.type);
  };

  const duplicateSubTaskTemplate = (s: SubTaskTemplate) => {
    const nextSubTask: SubTaskTemplate = {
      ...s,
      id: blockIdsToNewId[s.id],
      parentLayoutElement: s.parentLayoutElement
        ? blockIdsToNewId[s.parentLayoutElement]
        : null
    };
    if (nextSubTask.hooks.onComplete) {
      nextSubTask.hooks.onComplete = nextSubTask.hooks.onComplete.map((h) => {
        if (h.type === SubTaskHookType.NOTIFY_DATA_DESTINATION) {
          return {
            ...h,
            storages: h.storages.map((storage) => {
              console.log(
                'RESOURCES T',
                resourceIdToNewId,
                (storage as any).resourceId
              );
              return storage.type === 'GOOGLE_SHEET'
                ? {
                    ...storage,
                    resourceId: resourceIdToNewId[storage.resourceId]
                  }
                : storage.type === ExternalServiceType.MS_EXCEL
                ? {
                    ...storage,
                    resourceId: resourceIdToNewId[storage.resourceId]
                  }
                : storage.type === 'BAMBOO_HR' ||
                  storage.type === 'USER_PROFILE' ||
                  storage.type === ExternalServiceType.ADP_WORKFORCE_NOW ||
                  storage.type === ExternalServiceType.PERSONIO ||
                  storage.type ===
                    InternalStorageInstructionType.SUPPORTING_ACTOR
                ? storage
                : assertNever(storage);
            })
          };
        }
        return h;
      });
    }
    if (nextSubTask.type === 'QUESTION') {
      nextSubTask.data = {
        ...nextSubTask.data,
        storages: nextSubTask.data.storages.map((storage) => {
          console.log(
            'RESOURCES T',
            resourceIdToNewId,
            (storage as any).resourceId
          );
          return storage.type === 'GOOGLE_SHEET'
            ? {
                ...storage,
                resourceId: resourceIdToNewId[storage.resourceId]
              }
            : storage.type === ExternalServiceType.MS_EXCEL
            ? {
                ...storage,
                resourceId: resourceIdToNewId[storage.resourceId]
              }
            : storage.type === 'BAMBOO_HR' ||
              storage.type === 'USER_PROFILE' ||
              storage.type === ExternalServiceType.ADP_WORKFORCE_NOW ||
              storage.type === ExternalServiceType.PERSONIO ||
              storage.type === InternalStorageInstructionType.SUPPORTING_ACTOR
            ? storage
            : assertNever(storage);
        })
      };
    }
    if (
      nextSubTask.type === 'SUBMIT' &&
      nextSubTask.data.to.type === 'GDRIVE'
    ) {
      nextSubTask.data = {
        ...nextSubTask.data,
        to: {
          ...nextSubTask.data.to,
          resourceId: resourceIdToNewId[nextSubTask.data.to.resourceId]
        }
      };
    }

    if (
      nextSubTask.type === 'SUBMIT' &&
      (nextSubTask.data.to.type === SubmitTargetType.SHAREPOINT ||
        nextSubTask.data.to.type === SubmitTargetType.ONEDRIVE)
    ) {
      nextSubTask.data = {
        ...nextSubTask.data,
        to: {
          ...nextSubTask.data.to,
          resourceId: resourceIdToNewId[nextSubTask.data.to.resourceId]
        }
      };
    }
    if (nextSubTask.type === 'BACKGROUND_CHECK_INVITE') {
      // BG-check resources are owned 1:1 by a subtask, not shared: the picker
      // (BackgroundCheckInvite.tsx writeSelection) mutates the resource in
      // place when the admin picks a package. Two subtasks pointing at the
      // same data.resourceId means picking on one overwrites the other (main-378).
      // Remap via resourceIdToNewId so duplicated subtasks land on their own
      // resource ids. Fall back to the original id when the map has no entry
      // (sample-content seeding builds its own map and doesn't include
      // BG-check subtasks today, so the fallback is safe there).
      const mapped =
        resourceIdToNewId[nextSubTask.data.resourceId] ?? nextSubTask.data.resourceId;
      nextSubTask.data = { ...nextSubTask.data, resourceId: mapped };
    }
    return nextSubTask;
  };

  return {
    ...t,
    id: shortid(),
    reminders: t.reminders.map<IReminderTemplate>((r) => ({
      ...r,
      id: shortid()
    })),
    subTasks: [],
    blocks: t.blocks.map((b) => {
      if (isLayoutTemplateBlock(b)) {
        return duplicateLayoutTemplate(b);
      }
      if (isSubTaskTemplateBlock(b)) {
        return duplicateSubTaskTemplate(b);
      }
      return { ...b, id: blockIdsToNewId[b.id] };
    }),
    audiences: t.audiences.map((a) => ({
      ...a,
      id: shortid(),
      blocks: a.blocks.map((bId) => blockIdsToNewId[bId]),
      completionCriteria: a.completionCriteria
    })),
    timing: cloneDeep(t.timing),
    customCompletionCriteria
  };
};

export const getDuplicate = (
  _tpl: IExperienceTemplate,
  createdBy: string,
  reuseSameTitle = false
) => {
  const newId = shortid();
  const n = now();
  const tpl = cloneDeep(_tpl);
  const resourceIdToNewId = tpl.resources.reduce<{ [oldId: string]: string }>(
    (m, r) => {
      m[r.resourceId] = shortid();
      return m;
    },
    {}
  );
  const taskTplIdToNewId: { [prevId: string]: string } = {};

  const adminTitle = tpl.adminTitle || tpl.title;
  const duplicate: Doc<IExperienceTemplate> = {
    id: newId,
    collection: FS.experienceTemplates,
    data: {
      ...tpl,
      id: newId,
      title: tpl.title,
      adminTitle: reuseSameTitle ? adminTitle : `Copy of ${adminTitle}`, // TODO translate
      description: { ...tpl.description },
      anchor: { ...tpl.anchor },
      createdAt: n,
      createdBy,
      updatedAt: n,
      updatedBy: createdBy,
      status: ExperienceTemplateStatus.DRAFT,
      // Archived actors are guaranteed unreferenced in this template (that's
      // what allowed them to be archived) and the duplicate starts as an
      // unlaunched draft, so there's no way to unarchive them there —
      // dropping them keeps the duplicate from inheriting an
      // impossible-to-restore actor.
      supportingActors: keyBy(
        getSelectableSupportingActors(tpl.supportingActors),
        'key'
      ),
      automations: tpl.automations.map(duplicateAutomationTemplate),
      tasks: tpl.tasks.map<ITaskTemplate>((t) => {
        const duplicatedTask = duplicateTaskTemplate(t, resourceIdToNewId);
        taskTplIdToNewId[t.id] = duplicatedTask.id;
        return duplicatedTask;
      }),
      resources: tpl.resources.map<IExperienceTemplateResource>((r) => {
        console.log('RESOURCES', resourceIdToNewId, r.resourceId);
        if (r.type === 'GOOGLE_SHEET') {
          return {
            ...r,
            resourceId: resourceIdToNewId[r.resourceId],
            docId: '',
            sheetId: '',
            docStatus: 'PENDING',
            sheetStatus: 'PENDING',
            additionalSaveParticipantToResourceMoments: [
              ...r.additionalSaveParticipantToResourceMoments
            ],
            v: 2
          };
        }

        if (r.type === TemplateResourceType.GOOGLE_DRIVE_FOLDER) {
          return {
            ...r,
            resourceId: resourceIdToNewId[r.resourceId],
            folderId: '',
            status: 'PENDING'
          };
        }

        if (r.type === TemplateResourceType.MS_EXCEL) {
          const result: IExperienceTemplateResourceExcel = {
            ...r,
            resourceId: resourceIdToNewId[r.resourceId],
            driveId: '',
            driveItemId: '',
            status: 'PENDING',
            additionalSaveParticipantToResourceMoments: [
              ...r.additionalSaveParticipantToResourceMoments
            ],
            v: 1
          };
          return result;
        }

        if (r.type === TemplateResourceType.MICROSOFT_ONEDRIVE_FOLDER) {
          const result: IExperienceTemplateResourceMicrosoftOneDriveFolder = {
            ...r,
            resourceId: resourceIdToNewId[r.resourceId],
            status: 'PENDING'
          };
          return result;
        }

        if (r.type === TemplateResourceType.MICROSOFT_SHAREPOINT_FOLDER) {
          const result: IExperienceTemplateResourceMicrosoftSharepointFolder = {
            ...r,
            resourceId: resourceIdToNewId[r.resourceId],
            folderId: '',
            sharepointSiteId: '',
            sharepointSiteName: '',
            driveId: '',
            driveName: '',
            status: 'PENDING'
          };
          return result;
        }
        if (r.type === TemplateResourceType.DOCUSIGN_ENVELOPE_TEMPLATE) {
          return {
            ...r,
            resourceId: resourceIdToNewId[r.resourceId]
          };
        }

        if (r.type === TemplateResourceType.GOOGLE_DOC_TEMPLATE) {
          return {
            ...r,
            resourceId: resourceIdToNewId[r.resourceId]
          };
        }

        if (r.type === TemplateResourceType.BACKGROUND_CHECK_PACKAGE) {
          return {
            ...r,
            resourceId: resourceIdToNewId[r.resourceId]
          };
        }

        return assertNever(r);
      }),
      messages: {
        ...tpl.messages,
        other: tpl.messages.other.map((m) => duplicateMessageTemplate(m))
      },
      employeeApp: {
        ...tpl.employeeApp
      },
      previewToken: {
        ...tpl.previewToken,
        token: generateAccessToken()
      }
    }
  };
  const updateTriggerDependencies = (
    triggers: IItemTrigger[]
  ): IItemTrigger[] => {
    return triggers.map((x) => {
      if (x.itemType === 'TASK') {
        return {
          ...x,
          itemId: taskTplIdToNewId[x.itemId]
        };
      }
      return x;
    });
  };
  duplicate.data.tasks.forEach((x) => {
    if (x.timing.type === 'triggered') {
      x.timing.dependentOn = updateTriggerDependencies(x.timing.dependentOn);
    }
  });
  getAllMessageTemplates(duplicate.data.messages).forEach((x) => {
    if (x.timing.type === 'triggered') {
      x.timing.dependentOn = updateTriggerDependencies(x.timing.dependentOn);
    }
  });
  duplicate.data.automations.forEach((x) => {
    if (x.timing.type === 'triggered') {
      x.timing.dependentOn = updateTriggerDependencies(x.timing.dependentOn);
    }
  });
  return duplicate;
};

export const duplicateTemplate = async (
  tpl: IExperienceTemplate,
  createdBy: string
) => {
  const duplicate = getDuplicate(tpl, createdBy);
  traverse(duplicate, (p, v) => {
    if (v === undefined) {
      console.log('Faulty duplicate - path is undefined', p);
    }
  });
  await setDoc(duplicate);

  const enrollmentForm = await getEnrollmentFormByTemplate(tpl.orgId, tpl.id);
  if (enrollmentForm) {
    const duplicatedEnrollmentForm = getDuplicateEnrollmentForm(
      enrollmentForm.data,
      createdBy,
      duplicate.id
    );
    traverse(duplicatedEnrollmentForm, (p, v) => {
      if (v === undefined) {
        console.log('Faulty enrollment duplicate - path is undefined', p);
      }
    });
    await setDoc(duplicatedEnrollmentForm);
  }
  return duplicate;
};

export const deleteArchivedTemplate = async (doc: Doc<IExperienceTemplate>) => {
  if (doc.data.status !== ExperienceTemplateStatus.ARCHIVED) {
    throw new Error('NOT_AN_ARCHIVED_TEMPLATE');
  }
  await removeDoc(doc);
};

export const useExperienceTemplatesByIds = (ids: string[]) => {
  // TODO cache
  const listeners = useMemo(
    () => ids.map((id) => getExperienceTemplateListenerById(id)),
    [ids]
  );
  return useDocumentListeners(listeners);
};

export const useExperienceTemplateMapByIds = (ids: string[]) => {
  return useMappedLoadingValue(useExperienceTemplatesByIds(ids), (tpls) => {
    return Object.fromEntries(
      ids.map((id, i) => {
        const tpl = tpls[i] || null;
        return [id, tpl];
      })
    );
  });
};

export const getTemplateDuration = (tpl: Doc<IExperienceTemplate>) => {
  const end = getExpectedEndFromTemplate(tpl, tsFromMillis(0));
  return end.toMillis();
};

/**
 * True when two template-level timings are both trigger-based and wait on the exact
 * same set of tasks/conditions — e.g. for the message Task List preview, deciding
 * whether a trigger-based task "starts at the same time" as a trigger-based message.
 * Template timing has no resolved date to compare for trigger-based items (that only
 * exists once an actual experience instance resolves it), so this compares the trigger
 * condition itself instead. condition is always 'COMPLETE' and itemType always 'TASK'
 * today, so this reduces to comparing the sets of triggering item ids.
 */
export const templateTimingsShareTriggerCondition = (
  a: IItemTemplateTiming,
  b: IItemTemplateTiming
): boolean => {
  if (!isEventDrivenTiming(a) || !isEventDrivenTiming(b)) return false;
  if (a.type !== 'triggered' || b.type !== 'triggered') return false;
  if (a.dependentOn.length !== b.dependentOn.length) return false;
  const aItemIds = new Set(a.dependentOn.map((trig) => trig.itemId));
  return b.dependentOn.every((trig) => aItemIds.has(trig.itemId));
};

export const createUpcomingAutomationInstance = (
  experienceId: string,
  a: IAutomationTemplate,
  participant: Doc<IUser> | 'EVERYONE', // this is an escape hatch to build preview instances
  instanceAnchorDate: Timestamp,
  at: Timestamp,
  opts: { ignoreAudience?: boolean } = {}
) => {
  const audience =
    participant === 'EVERYONE'
      ? getEveryoneAudience(getEffectiveAudiences(a))
      : getAudienceForUser(getEffectiveAudiences(a), participant, instanceAnchorDate);
  const actionTpls = opts.ignoreAudience
    ? a.actions
    : a.actions.filter((b) => audience.blocks.includes(b.id));

  if (!actionTpls.length) {
    console.log('NO_ACTIONS_FOR_USER - SKIPPING_AUTOMATION', {
      audience,
      automationTpl: a,
      participant
    });
    return null;
  }
  return {
    id: shortid(),
    experienceId,
    templateId: a.id,
    status: AutomationStatus.UPCOMING,
    completedAt: null,
    overrides: null,
    audience: audience.id,
    actions: keyBy(
      actionTpls.map<AutomationAction>((ac) => ({
        id: shortid(),
        templateId: ac.id,
        type: ac.type,
        status: AutomationActionStatus.UPCOMING,
        data: null,
        errorData: null,
        doneAt: null
      })),
      (ac) => ac.templateId
    ),
    timing: {
      at,
      manualOverride: false
    }
  };
};

export const toAutomationInstances = (
  experienceId: string,
  automationTpls: IAutomationTemplate[],
  participant: Doc<IUser> | 'EVERYONE', // this is an escape hatch to build preview instances
  instanceAnchorDate: Timestamp,
  tplAnchor: ExperienceAnchor,
  tz: string,
  otherTasks: ITaskTemplate[],
  enrollAt: Timestamp,
  opts: { ignoreAudience?: boolean; includeTriggeredItems?: boolean } = {}
): { [instanceId: string]: IAutomation } => {
  return keyBy(
    compact(
      automationTpls.map<IAutomation | null>((a) => {
        if (isEventDrivenTiming(a.timing) && !opts.includeTriggeredItems) {
          return null;
        }

        const at = getFreshNonTaskStartDate(
          getFixedTimingOrApproximateParentTiming(a.timing, otherTasks),
          instanceAnchorDate,
          tplAnchor,
          tz
        );

        const nextAut = createUpcomingAutomationInstance(
          experienceId,
          a,
          participant,
          instanceAnchorDate,
          at,
          opts
        );

        if (
          nextAut &&
          skipAllBecauseCatchupModeIsDisabled(a, enrollAt, nextAut.timing.at)
        ) {
          nextAut.status = AutomationStatus.SKIPPED;
          nextAut.actions = mapValues(nextAut.actions, (ac) => ({
            ...ac,
            status: AutomationActionStatus.SKIPPED
          }));
        }

        return nextAut;
      })
    ),
    (t) => t.templateId
  );
};

export const createNotStartedMessageStatus = (
  msg: ExperienceTemplateMessage,
  participant: Doc<IUser>,
  at: Timestamp,
  referenceDate: Timestamp
) => {
  const audience = getAudienceForUser(
    getEffectiveAudiences(msg),
    participant,
    referenceDate
  );
  if (!audience.blocks.length) {
    return null;
  }
  return {
    status: MessageStatus.UPCOMING,
    overrides: { at: null },
    audience: audience.id,
    timing: {
      at,
      manualOverride: false
    }
  };
};

export const createNotStartedTask = (
  expId: string,
  t: ITaskTemplate,
  participant: Doc<IUser>,
  dates: { start: Timestamp; end: Timestamp },
  referenceDate: Timestamp
): ITask | null => {
  const audience = getAudienceForUser(
    getEffectiveAudiences(t),
    participant,
    referenceDate
  );
  const subTasks: ISubTask[] = filterBlocksForAudience(
    getSubTaskTemplates(t),
    audience
  ).map<ISubTask>((s) => ({
    id: shortid(),
    templateId: s.id,
    type: s.type,
    status: SubTaskStatus.OPEN,
    completedAt: null,
    requirementType: s.requirementType,
    parentLayoutElement: s.parentLayoutElement
  }));

  if (!subTasks.length) {
    console.log('NO_SUBTASK_FOR_USER - SKIPPING_TASK', {
      audience,
      taskTpl: t,
      participant
    });
    return null;
  }
  return {
    id: shortid(),
    experienceId: expId,
    templateId: t.id,
    status: TaskStatus.NOT_STARTED,
    actor: t.actor,
    audience: audience.id,
    overrides: null,
    subTasks,
    timing: {
      ...dates,
      manualOverride: false
    }
  };
};

export const getExpectedEndFromTemplate = (
  tpl: Doc<IExperienceTemplate>,
  startsAt: Timestamp
) => {
  // TODO TRIGGERED ITEMS
  // How do they influence the end?
  // We probably need to look at the end of the parent task, then add the duration for those
  // which have a relative end date. otherwise we can also just look at the end.
  return addToTs(
    startsAt,
    Math.max(
      0,
      ...tpl.data.tasks.map((t) =>
        t.timing.type === 'triggered'
          ? t.timing.end.duration // TODO Triggered Items - this would need to look at the parent trigger. For this purpose both end types are treated the same
          : t.timing.type === 'dataChange'
          ? (t.timing.earliestStart ?? 0)
          : t.timing.end
      ),
      ...getAllMessageTemplates(tpl.data.messages).map((m) =>
        // the triggered item does not need to be considered, as it's timing
        // is practically equivalent to its trigger
        isEventDrivenTiming(m.timing) ? 0 : m.timing.start
      ),
      ...tpl.data.automations.map((a) =>
        // the triggered item does not need to be considered, as it's timing
        // is practically equivalent to its trigger
        isEventDrivenTiming(a.timing) ? 0 : a.timing.start
      )
    )
  );
};

export const skipAllBecauseCatchupModeIsDisabled = <
  X extends { catchup: { disabled: boolean } }
>(
  item: X,
  enrollAt: Timestamp,
  sendOrRunDate: Timestamp
) => {
  return (
    item.catchup.disabled &&
    tsIsAfter(addToTs(enrollAt, -ONE_DAY), sendOrRunDate)
  );
};

export const createExperienceInstance = (
  tpl: Doc<IExperienceTemplate>,
  userActors: {
    key: string;
    user: Doc<IUser> | null; // - ideally always defined, but be defensive!
  }[],
  startsAt: Timestamp,
  enrollAt: Timestamp,
  source: EnrollSource,
  opts: { includeTriggeredItemsAsFixedTimmingItems?: boolean } = {}
) => {
  const includeTriggeredItemsAsFixedTimmingItems = !!opts.includeTriggeredItemsAsFixedTimmingItems;

  const anchorDate = addToTs(
    startsAt,
    getAnchorOffsetForExperience(tpl.data.anchor)
  );
  const expectedEnd = getExpectedEndFromTemplate(tpl, startsAt);

  const expActors = [
    ...userActors.map<ExperienceUserActor>((a) => ({
      type: ExperienceActorType.USER,
      key: a.key,
      userId: a.user?.id || '',
      accessToken: generateAccessToken()
    })),
    ...compact(
      Object.values(tpl.data.supportingActors).map((sa) => {
        if (sa.type === SupportingActorType.CHANNEL) {
          const expChannelActor: ExperienceChannelActor = {
            type: ExperienceActorType.CHANNEL,
            key: sa.key,
            accessToken: generateAccessToken() /// should we actually genrate one?
          };
          return expChannelActor;
        }
        return null;
      })
    )
  ];

  const usersByActor = mapValues(
    keyBy(userActors, (a) => a.key),
    (x) => x.user
  );

  const participant = usersByActor[PARTICIPANT_ACTOR_KEY];
  if (!participant) {
    console.error('PARTICIPANT_NOT_FOUND', { tpl, actors: userActors });
    throw new Error('CANNOT_CREATE_INSTANCE-NO_PARTICIPANT');
  }

  const experienceId = shortid();
  const enrollEvent: IExperienceEvent = {
    id: shortid(),
    type: ExperienceEventType.EXP_ENROLL,
    at: enrollAt,
    initiator: 'EMPLEXP',
    source
  };

  const skippableMessages = tpl.data.messages.other.filter((m) => {
    const audience = getAudienceForUser(
      getEffectiveAudiences(m),
      participant,
      anchorDate
    );
    return !audience.blocks.length;
  });

  const launchAt =
    compareTimestamps(enrollAt, startsAt) < 0 ? startsAt : enrollAt;

  const data: IExperience = {
    id: experienceId,
    orgId: tpl.data.orgId,
    templateId: tpl.id,
    userIds: expActors.filter(isExperienceUserActor).map((a) => a.userId),
    accessTokens: expActors.map((a) => a.accessToken),
    anchorOffsetOverride: null,
    anchorDate: {
      at: anchorDate
    },
    messageOverrides: fromPairs(
      skippableMessages.map((m) => [m.id, { skip: true }])
    ),
    messagesStatuses: fromPairs(
      compact(
        tpl.data.messages.other.map((m) => {
          if (
            isEventDrivenTiming(m.timing) &&
            !includeTriggeredItemsAsFixedTimmingItems
          ) {
            return null;
          }
          const tplSupportingActor = tpl.data.supportingActors[m.actor];

          const user = usersByActor[m.actor];
          if (
            !user &&
            tplSupportingActor?.type !== SupportingActorType.CHANNEL
          ) {
            console.warn('USER_NOT_FOUND - SKIPPING MESSAGE', {
              msgTemplate: m,
              experienceId
            });
            return null;
          }

          const timing = getFixedOrConvertToMessageFixedTimingConsideringTriggerChain(
            m.timing,
            tpl.data
          );

          const nextStatus = createNotStartedMessageStatus(
            m,
            participant,
            getFreshNonTaskStartDate(
              timing,
              anchorDate,
              tpl.data.anchor,
              tpl.data.tz
            ),
            anchorDate
          );

          if (!nextStatus) {
            return null;
          }

          if (
            skipAllBecauseCatchupModeIsDisabled(
              m,
              enrollAt,
              nextStatus.timing.at
            )
          ) {
            nextStatus.status = MessageStatus.SKIPPED;
          }

          return [m.id, nextStatus];
        })
      )
    ),
    actors: expActors,
    ignoredMissingActors: Object.values(tpl.data.supportingActors).reduce<{
      [actorKey: string]: IIgnoredMissingActor;
    }>((m, act) => {
      if (act.ignoreWhenMissing) {
        m[act.key] = {
          key: act.key,
          ignored: act.ignoreWhenMissing,
          ignoredBy: '',
          ignoredAt: null
        };
      }
      return m;
    }, {}),
    ignoredVariables: [],
    status: ExperienceStatus.NOT_STARTED, // updated in fs hook
    startsAt,
    expectedEnd,
    launchAt,
    createdAt: enrollAt,
    finishedAt: null,
    events: [enrollEvent],
    lastEvent: enrollEvent,
    messages: [],
    tasks: compact(
      tpl.data.tasks.map<ITask | null>((t) => {
        if (
          isEventDrivenTiming(t.timing) &&
          !includeTriggeredItemsAsFixedTimmingItems
        ) {
          return null;
        }
        const user = usersByActor[t.actor];
        if (!user) {
          console.warn('USER_NOT_FOUND - SKIPPING TASK', {
            taskTemplate: t,
            experienceId
          });
          return null;
        }

        const timing = getFixedOrConvertToTaskFixedTimingConsideringTriggerChain(
          t.timing,
          tpl.data
        );

        const nextTask = createNotStartedTask(
          experienceId,
          t,
          participant,
          getFreshTaskDates(timing, anchorDate, tpl.data.anchor, tpl.data.tz),
          anchorDate
        );

        if (
          nextTask &&
          skipAllBecauseCatchupModeIsDisabled(
            t,
            enrollAt,
            nextTask.timing.start
          )
        ) {
          nextTask.subTasks.forEach((s) => {
            s.status = SubTaskStatus.SKIPPED;
          });
          nextTask.status = TaskStatus.SKIPPED;
        }

        return nextTask;
      })
    ),
    instanceErrors: [],
    automations: toAutomationInstances(
      experienceId,
      tpl.data.automations,
      participant,
      anchorDate,
      tpl.data.anchor,
      tpl.data.tz,
      tpl.data.tasks,
      enrollAt
    ),
    v: 1
  };

  return {
    id: experienceId,
    collection: FS.experiences,
    data
  };
};

/**
 * Manual enrollment via the BE service. Replaces the legacy direct
 * `batchSetDocs(experiences)` write that bypassed the enrollment service —
 * which caused the new onCreate trigger to skip dataChange evaluation
 * (audit ticket new-main-e3t / l1r).
 *
 * Each instance is enrolled via `experiences_enrollParticipant` (onCall).
 * The BE service handles atomic instance + schedules + dataChange items
 * inside one transaction with the `_mutationSource` stamp set, so the
 * onCreate trigger correctly skips its legacy fan-out.
 *
 * Per-participant overrides (anchor date, supporting actors) are derived
 * from the instance's `fieldOverrides` audit map — which the modal
 * already populates via `getFieldOverridesFromChanges`. Only fields the
 * admin actually edited are sent as overrides.
 */
// Bulk manual enroll fires ONE `experiences_enrollParticipant` callable per
// participant. Firing them all at once (the previous `Promise.allSettled(map)`)
// saturated the browser's per-host connection pool AND burst-scaled the enroll
// function (minInstances:1), so large batches (~150+) timed out / failed. Cap the
// number of in-flight callables and drain in sequential chunks.
const ENROLL_CONCURRENCY = 8;

export const enrollUsers = async (
  experiences: Doc<IExperience>[],
  adminUserId: string
) => {
  const results = await mapWithConcurrencySettled(
    experiences,
    ENROLL_CONCURRENCY,
    async (e) => {
      const participantActor = e.data.actors?.find(
        (a) => a.key === PARTICIPANT_ACTOR_KEY
      );
      if (!participantActor || participantActor.type !== ExperienceActorType.USER) {
        throw new Error(`enrollUsers: instance ${e.id} has no participant actor`);
      }
      const participantUserId = participantActor.userId;
      const overrides = e.data.fieldOverrides || {};

      // Anchor override: only send when the modal flagged it as edited
      const anchorWasEdited = !!overrides[ANCHOR_OVERRIDE_KEY]?.active;
      const anchorDateOverride = anchorWasEdited
        ? e.data.anchorDate.at.toMillis()
        : undefined;

      // Actor overrides: only the keys the modal flagged as edited
      const supportingActorOverrides: Record<string, string> = {};
      Object.keys(overrides).forEach((k) => {
        if (!overrides[k]?.active) return;
        if (!k.startsWith(ACTOR_OVERRIDE_KEY_PREFIX)) return;
        const actorKey = k.slice(ACTOR_OVERRIDE_KEY_PREFIX.length);
        const actor = e.data.actors?.find((a) => a.key === actorKey);
        if (actor && actor.type === ExperienceActorType.USER) {
          supportingActorOverrides[actorKey] = actor.userId;
        }
      });

      const enrollAt =
        e.data.events && e.data.events.length > 0
          ? e.data.events[0].at.toMillis()
          : Date.now();
      const startsAt = e.data.startsAt?.toMillis() ?? enrollAt;

      // v1 parity (bead new-main-q2k6): derive preSkips from the staged
      // instance's mutated statuses. See `derivePreSkipsFromStagedInstance`
      // above for the rationale.
      const derivedPreSkips = derivePreSkipsFromStagedInstance(e);

      // v1 parity: derive per-item "Start today"/"Send today"/"Run today" timing
      // moves from the staged instance (manualOverride'd items). v1 batch-wrote
      // the staged instance so these persisted; the v2 onCall enroll dropped them.
      const derivedTimingOverrides = deriveTimingOverridesFromStagedInstance(e);

      const result = await callFirebaseFunction<{
        instanceId: string;
        skipped?: { reason: string };
      }>('experiences_enrollParticipant', {
        participantUserId,
        templateId: e.data.templateId,
        enrollAt,
        startsAt,
        anchorDateOverride,
        supportingActorOverrides:
          Object.keys(supportingActorOverrides).length > 0
            ? supportingActorOverrides
            : undefined,
        preSkips: derivedPreSkips.length > 0 ? derivedPreSkips : undefined,
        timingOverrides:
          derivedTimingOverrides.length > 0 ? derivedTimingOverrides : undefined
      });

      const enrollAction: ManualEnrollmentAction = {
        action: CUSTOMER_ACTIONS.MANUAL_ENROLLMENT,
        adminUserId,
        instanceId: result.instanceId,
        templateId: e.data.templateId,
        orgId: e.data.orgId
      };
      tryReportCustomerAction(enrollAction);

      return result;
    }
  );

  const failures = results.filter((r) => r.status === 'rejected');
  if (failures.length > 0) {
    // Surface a representative error for the caller's UI snackbar.
    const firstFailure = failures[0] as PromiseRejectedResult;
    throw firstFailure.reason;
  }
};

export type SelectableParticipantWithActors = {
  participant: Doc<IUser>;
  missingActors: string[];
  supportingActors: {
    key: string;
    user: Doc<IUser>;
  }[];
  ignoredMissingActors: IIgnoredMissingActor[];
};

export type SelectableParticipantWithActorsAnalysis = {
  previouslyEnrolled: SelectableParticipantWithActors[];
  selectable: SelectableParticipantWithActors[];
  matchingTheTemplateRule: SelectableParticipantWithActors[];
  hasRule: boolean;
};

export const collectSupportingActorsForParticipant = (
  supportingActorDefs: ISupportingActor[],
  usersById: { [userId: string]: Doc<IUser> },
  participant: Doc<IUser>
) => {
  const missingActors: string[] = [];
  const supportingActors: SelectableParticipantWithActors['supportingActors'] = compact(
    supportingActorDefs.map((a) => {
      if (a.type === 'staticUser') {
        const userId =
          a.ruleBasedUserIds.find((r) => matchUser(participant, r.rule, now())) // Worth considering if we need to pass something else down here
            ?.userId || a.defaultUserId;
        const otherUser = usersById[userId];
        if (otherUser) {
          return {
            user: otherUser,
            key: a.key
          };
        }
      }
      if (a.type === 'user' && a.source === 'PROFILE') {
        const otherUserId = get(participant.data as any, a.field);
        if (otherUserId && typeof otherUserId === 'string') {
          const otherUser = usersById[otherUserId];
          if (otherUser) {
            return {
              user: otherUser,
              key: a.key
            };
          }
        }
      }
      // TOO SPAMMY - Only enable when checking specifically for this
      // console.warn('missing supporting actor', {
      //   user: participant,
      //   supportingActor: a
      // });
      missingActors.push(a.key);
      return null;
    })
  );
  return {
    participant,
    missingActors,
    supportingActors,
    ignoredMissingActors: []
  };
};

export const useSelectableParticipantsWithTheirSupportingActors = (
  d: Doc<IExperienceTemplate>
): LoadingValue<SelectableParticipantWithActorsAnalysis> => {
  const { enrollRule, supportingActors } = d.data;
  const actors = useMemo(() => Object.values(supportingActors), [
    supportingActors
  ]);
  const mapFn = useCallback(
    ([instances, us]: [Doc<IExperience>[], Doc<IUser>[]]) => {
      const prevParticipants = compact(
        instances.map((i) => getParticipantActor(i))
      );
      const presentUsers = new Set(prevParticipants.map((p) => p.userId));
      const usersById = keyBy(us, (u) => u.id);
      const allParticipantsAndTheirActors = us
        .filter((u) => u.data.active)
        .map((u) =>
          collectSupportingActorsForParticipant(actors, usersById, u)
        );
      const [
        previouslyEnrolled,
        selectable
      ] = partition(allParticipantsAndTheirActors, (p) =>
        presentUsers.has(p.participant.id)
      );
      return {
        previouslyEnrolled,
        selectable,
        matchingTheTemplateRule: enrollRule
          ? selectable.filter((u) =>
              matchUser(u.participant, enrollRule, now())
            )
          : selectable,
        hasRule: !!enrollRule
      };
    },
    [enrollRule, actors]
  );
  return useMappedLoadingValue(
    combineLoadingValues(
      useExperienceInstancesCurrentlyRelevant(
        d.data.orgId,
        d.id,
        d.data.expectedEndThreshold
      ),
      useUsersByOrgId(d.data.orgId)
    ),
    mapFn,
    true
  );
};

// ---------------------------------------------------------------------------
// Paginated, server-side selectable-participant picker (manual enroll).
//
// Replaces useSelectableParticipantsWithTheirSupportingActors (which loaded the
// ENTIRE org user collection client-side and hung large orgs — Aspen Dental =
// 17,771 users). Pages the new experiences_getSelectableParticipants onCall,
// which applies the same selection server-side (active, !enrolled, isSelectable
// unless includeUnselectable, the RuleBuilder filterRule, and searchTerm).
// ---------------------------------------------------------------------------

const PICKER_PAGE_LIMIT = 50;

export type SelectableParticipantsParams = {
  searchTerm?: string;
  filterRule?: Rule<UserField> | null;
  includeUnselectable?: boolean;
};

// onCall serializes admin Timestamps to {_seconds,_nanoseconds}; revive them so
// the picker's date columns + anchor/selectability logic see real Timestamps.
const rehydrateDeep = (v: any): any => {
  if (v == null || typeof v !== 'object') return v;
  const ts = refreshTimestamp(v);
  if (ts !== v) return ts; // refreshTimestamp converted a serialized timestamp
  if (Array.isArray(v)) return v.map(rehydrateDeep);
  const out: Record<string, any> = {};
  for (const k of Object.keys(v)) out[k] = rehydrateDeep(v[k]);
  return out;
};

const mapApiParticipant = (p: any): SelectableParticipantWithActors => ({
  participant: { ...p.participant, data: rehydrateDeep(p.participant.data) },
  supportingActors: (p.supportingActors || []).map((a: any) => ({
    key: a.key,
    user: { ...a.user, data: rehydrateDeep(a.user.data) }
  })),
  missingActors: p.missingActors || [],
  ignoredMissingActors: []
});

export const useSelectableParticipantsPaged = (
  tpl: Doc<IExperienceTemplate>,
  params: SelectableParticipantsParams
): {
  participants: SelectableParticipantWithActors[];
  loadMore: () => void;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
} => {
  const { searchTerm, filterRule, includeUnselectable } = params;
  const [participants, setParticipants] = useState<SelectableParticipantWithActors[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // Guards against out-of-order responses when params change mid-flight.
  const reqRef = useRef(0);

  const fetchPage = useCallback(
    async (nextCursor: string | null, reset: boolean) => {
      const reqId = ++reqRef.current;
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      try {
        const res = await callFirebaseFunction<{
          participants: any[];
          nextCursor: string | null;
          done: boolean;
        }>('experiences_getSelectableParticipants', {
          orgId: tpl.data.orgId,
          templateId: tpl.id,
          cursor: nextCursor,
          limit: PICKER_PAGE_LIMIT,
          searchTerm: searchTerm || undefined,
          filterRule: filterRule || undefined,
          includeUnselectable: !!includeUnselectable
        });
        if (reqId !== reqRef.current) return; // a newer request superseded this one
        const mapped = res.participants.map(mapApiParticipant);
        setParticipants((prev) => (reset ? mapped : [...prev, ...mapped]));
        setCursor(res.nextCursor);
        setHasMore(!res.done && !!res.nextCursor);
      } finally {
        if (reqId === reqRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [tpl.id, tpl.data.orgId, searchTerm, filterRule, includeUnselectable]
  );

  // Reset + fetch the first page whenever the query params change.
  useEffect(() => {
    setParticipants([]);
    setCursor(null);
    setHasMore(true);
    fetchPage(null, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore || !cursor) return;
    fetchPage(cursor, false);
  }, [loading, loadingMore, hasMore, cursor, fetchPage]);

  // Auto-continue until the first page is actually full (or the scan is
  // exhausted). A search / filter is sparse: a server call can return zero
  // matches yet `hasMore: true` (it scanned a window without hits). Drive the
  // next page automatically instead of stranding the user on an empty list —
  // there is no scroll sentinel to fire loadMore when nothing rendered.
  // Scroll-driven loadMore takes over once we have a full page.
  useEffect(() => {
    if (
      !loading &&
      !loadingMore &&
      hasMore &&
      cursor &&
      participants.length < PICKER_PAGE_LIMIT
    ) {
      fetchPage(cursor, false);
    }
  }, [loading, loadingMore, hasMore, cursor, participants.length, fetchPage]);

  return { participants, loadMore, hasMore, loading, loadingMore };
};

export const getAllActorsForSelectableParticipant = (
  u: SelectableParticipantWithActors,
  tpl: Doc<IExperienceTemplate>
) => {
  return {
    participant: {
      key: PARTICIPANT_ACTOR_KEY,
      user: u.participant,
      missingActors: u.missingActors.map((a) => tpl.data.supportingActors[a])
    },
    supportingActors: u.supportingActors
  };
};

export const getAllActorsForSelectableParticipants = (
  xs: SelectableParticipantWithActors[],
  tpl: Doc<IExperienceTemplate>
) => {
  return xs.map((u) => getAllActorsForSelectableParticipant(u, tpl));
};

export const getTaskTemplateById = (
  tpl: Doc<IExperienceTemplate>,
  taskTplId: string
): ITaskTemplate | null => {
  return tpl.data.tasks.find((t) => t.id === taskTplId) || null;
};

export const getTaskTemplateForTaskId = (
  tpl: Doc<IExperienceTemplate>,
  exp: Doc<IExperience>,
  taskId: string
): ITaskTemplate | null => {
  const task = exp.data.tasks.find((t) => t.id === taskId);
  if (!task) {
    return null;
  }
  return getTaskTemplateById(tpl, task.templateId);
};

export const getAllMessageTemplates = (
  msgs: IExperienceTemplate['messages']
) => {
  return compact([...msgs.other, msgs.outro]);
};

export const getMessageTemplateById = (
  tpl: Doc<IExperienceTemplate>,
  msgId: string
) => {
  return (
    getAllMessageTemplates(tpl.data.messages).find((m) => m.id === msgId) ||
    null
  );
};

export const getAutomationTemplateById = (
  tpl: Doc<IExperienceTemplate>,
  automationId: string
) => {
  return tpl.data.automations.find((a) => a.id === automationId) || null;
};

export const getStartDatesConnectedToAMessageByActorFromAbsoluteDates = (
  experienceStart: Timestamp,
  msgs: ExperienceTemplateMessage[],
  timings: { [msgId: string]: { timing: { at: Timestamp } } }
) => {
  return msgs.reduce<{
    [actor: string]: Set<number>;
  }>((m, msg) => {
    if (!msg.taskList.range) {
      return m;
    }
    const timing = timings[msg.id]?.timing;
    if (!timing) {
      return m;
    }
    const container = (m[msg.actor] = m[msg.actor] || new Set());
    container.add(timing.at.toMillis() - experienceStart.toMillis());
    return m;
  }, {});
};

export const getStartDatesConnectedToAMessageByActorFromRelativeDates = (
  msgs: ExperienceTemplateMessage[]
) => {
  return msgs.reduce<{
    [actor: string]: Set<number>;
  }>((m, msg) => {
    if (
      !msg.taskList.range ||
      msg.batchMode !== BatchMode.DEFAULT ||
      isEventDrivenTiming(msg.timing)
    ) {
      return m;
    }
    const container = (m[msg.actor] = m[msg.actor] || new Set());
    container.add(msg.timing.start);
    return m;
  }, {});
};

export const tasksToGetTaskConnectorArgs = (tasks: ITaskTemplate[]) => {
  return compact(
    tasks.map((t) => {
      if (t.batchMode !== BatchMode.DEFAULT || t.timing.type !== 'fixed') {
        return null;
      }
      return { id: t.id, actor: t.actor, start: t.timing.start };
    })
  );
};

export const getTaskConnectors = <
  T extends { id: string; actor: string; start: number }
>(
  tasks: T[],
  connectionPoints: { [actor: string]: Set<number> }
) => {
  const byActorAndStart = tasks.reduce<{
    [actorAndStart: string]: string[];
  }>((m, t) => {
    if (connectionPoints[t.actor]?.has(t.start)) {
      const key = `${t.actor}-${t.start}`;
      const container = (m[key] = m[key] || []);
      container.push(t.id);
    }
    return m;
  }, {});

  return Object.values(byActorAndStart).reduce<{
    [taskId: string]: 'L' | 'T' | undefined;
  }>((m, x) => {
    x.forEach((tId, i) => {
      const connector: 'L' | 'T' = i === x.length - 1 ? 'L' : 'T';
      m[tId] = connector;
    });
    return m;
  }, {});
};

export const getMessageBatchesForTemplate = (
  d: Doc<IExperienceTemplate>
): {
  message: ExperienceTemplateMessage;
  tasks: ITaskTemplate[];
}[] => {
  const res: {
    message: ExperienceTemplateMessage;
    tasks: ITaskTemplate[];
  }[] = [];
  const tasksByActorAndSameStart: {
    [key: string]: { [start: string]: ITaskTemplate[] };
  } = {};
  const tasksByActorAndSameTrigger: {
    [key: string]: { [triggers: string]: ITaskTemplate[] };
  } = {};

  d.data.tasks.forEach((t) => {
    if (!t.timing) {
      // Malformed task without timing — skip rather than crash. Seen on a
      // small number of legacy templates where `timing` was lost during a
      // migration. Treated like dataChange (not grouped for batches).
      return;
    }
    if (t.timing.type === 'fixed') {
      const aC = (tasksByActorAndSameStart[t.actor] =
        tasksByActorAndSameStart[t.actor] || {});
      const tC = (aC[t.timing.start] = aC[t.timing.start] || []);
      tC.push(t);
    } else if (t.timing.type === 'triggered') {
      const triggerKey = t.timing.dependentOn
        .map((x) => x.itemId)
        .sort()
        .join('-');
      const aC = (tasksByActorAndSameTrigger[t.actor] =
        tasksByActorAndSameTrigger[t.actor] || {});
      const tC = (aC[triggerKey] = aC[triggerKey] || []);
      tC.push(t);
    }
    // dataChange tasks are not grouped for batch connections
  });

  getAllMessageTemplates(d.data.messages).forEach((msg) => {
    if (!msg.taskList.range || msg.batchMode !== BatchMode.DEFAULT) {
      return;
    }
    if (!msg.timing) {
      return;
    }
    if (msg.timing.type === 'triggered') {
      const triggerKey = msg.timing.dependentOn
        .map((x) => x.itemId)
        .sort()
        .join('-');
      const tasks = tasksByActorAndSameTrigger[msg.actor]?.[triggerKey] || [];
      if (tasks.length) {
        res.push({ message: msg, tasks });
      }
    } else if (msg.timing.type === 'fixed') {
      const tasks =
        tasksByActorAndSameStart[msg.actor]?.[msg.timing.start] || [];
      if (tasks.length) {
        res.push({ message: msg, tasks });
      }
    }
    // dataChange messages are not grouped for batch connections
  });

  return res;
};

export const getMessageBatchConnections = (d: Doc<IExperienceTemplate>) => {
  const messageBatches = getMessageBatchesForTemplate(d);

  const connectUpIds = new Set(
    flatten(messageBatches.map((x) => x.tasks.map((t) => t.id)))
  );
  const connectDownIds = new Set(
    flatten(
      messageBatches.map((x) => [
        x.message.id,
        ...x.tasks.map((t) => t.id).slice(0, -1)
      ])
    )
  );

  return { connectUpIds, connectDownIds };
};

export const getExperienceTemplatesAsMap = (ids: string[]) => {
  return getAllByIdAsMap(
    store().collection(FS.experienceTemplates),
    ids,
    toExperienceTemplateDoc
  );
};

export type TimelineItemsByActor = {
  [actorKey: string]: {
    tasks: ITaskTemplate[];
    messages: ExperienceTemplateMessage[];
  };
};

export const humanReadableActorName = (
  actorKey: string,
  supportingActors: IExperienceTemplate['supportingActors'] = {}
) => {
  return (
    supportingActors[actorKey]?.name ||
    actorKey
      .split(/(?=[A-Z])/)
      .map(startCase)
      .join(' ')
  );
};

export const groupTimelineItemsByActor = (
  tpl: Doc<IExperienceTemplate>
): TimelineItemsByActor => {
  const res: TimelineItemsByActor = {};
  const getContainerOrDefault = (actorKey: string) => {
    return (res[actorKey] = res[actorKey] || { tasks: [], messages: [] });
  };
  tpl.data.tasks.forEach((task) => {
    const container = getContainerOrDefault(task.actor);
    container.tasks.push(task);
  });
  getAllMessageTemplates(tpl.data.messages).forEach((message) => {
    const container = getContainerOrDefault(message.actor);
    container.messages.push(message);
  });
  return res;
};

export const hasActorTimelineItems = (
  actorKey: string,
  items: TimelineItemsByActor
) => {
  const container = items[actorKey] || { tasks: [], messages: [] };
  return !!(container.messages.length || container.tasks.length);
};

export const approximateIfAggregationForMessageIsPossible = (
  msg: ExperienceTemplateMessage,
  anchorProperty?: string
) => {
  const r = mergeInterpolationAnalysisResults(
    analyzeExperienceInterpolation(msg.subject),
    analyzeExperienceInterpolationsInBlocks(msg.blocks)
  );

  // Using variables of the recipient actor is fine
  const actors = Object.keys(r.actorVariables).filter((a) => a !== msg.actor);
  const partVars = [...(r.actorVariables[PARTICIPANT_ACTOR_KEY] || new Set())];
  return (
    // or others
    partVars.every((x) => anchorProperty && x === anchorProperty) &&
    actors.length < 2
  );
};

export const hasInstanceSpecificInterpolations = (
  str: string,
  anchorProperty?: string
) => {
  const r = analyzeExperienceInterpolation(str);

  const partVars = [...(r.actorVariables[PARTICIPANT_ACTOR_KEY] || new Set())];
  return !partVars.every((x) => anchorProperty && x === anchorProperty);
};

export const getAnchorInterpolationVariable = (
  anchor: ExperienceAnchor,
  anchorFields: AnchorField[]
) => {
  return anchor.type === ExperienceAnchorType.EMPLOYEE_DATE
    ? getUserFieldForAnchor(anchorFields, anchor.field)
    : undefined;
};

export const getTriggeredItemsForTask = (
  tpl: Doc<IExperienceTemplate>,
  taskTplId: string
): ITimelineItem[] => {
  const isDependentOnTask = (timing: IItemTemplateTiming) =>
    timing.type === 'triggered' &&
    !!timing.dependentOn.find(
      (x) => x.itemType === 'TASK' && x.itemId === taskTplId
    );
  return compact([
    ...getAllMessageTemplates(tpl.data.messages).map<ITimelineItem | null>(
      (m) => (isDependentOnTask(m.timing) ? { itemType: 'MSG', item: m } : null)
    ),
    ...tpl.data.tasks.map<ITimelineItem | null>((t) => {
      return isDependentOnTask(t.timing)
        ? {
            itemType: 'TASK',
            item: t
          }
        : null;
    }),
    ...tpl.data.automations.map<ITimelineItem | null>((a) =>
      isDependentOnTask(a.timing) ? { itemType: 'AUTOMATION', item: a } : null
    )
  ]);
};

export const getActorSortKey = (actorKey: string | null) => {
  if (actorKey === null) {
    return 'ZZZZZZZZZZ';
  }
  if (actorKey === PARTICIPANT_ACTOR_KEY) {
    return 'aaaaaaaaaa';
  }
  if (actorKey === 'manager' || actorKey === 'mentor') {
    return 'aaaaaaaaab';
  }
  return actorKey;
};

export const getTriggerHierarchy = (
  d: Doc<IExperienceTemplate>
): {
  cause: ITaskTemplate;
  effects: ITimelineItem[];
}[] => {
  // naive approach first - speed up later
  return compact(
    d.data.tasks.map((t) => {
      const effects = orderBy(getTriggeredItemsForTask(d, t.id), [
        (x) => {
          return getActorSortKey(
            x.itemType === 'AUTOMATION' ? null : x.item.actor
          );
        },
        (x) => {
          return x.itemType === 'MSG' ? 0 : x.itemType === 'TASK' ? 1 : 2;
        }
      ]);
      if (!effects.length) {
        return null;
      }
      return { cause: t, effects };
    })
  );
};

export const getTriggerHierarchyByEffect = (d: Doc<IExperienceTemplate>) => {
  const hierarchy = getTriggerHierarchy(d);
  const res: {
    automations: { [tplId: string]: string[] };
    messages: { [tplId: string]: string[] };
    tasks: { [tplId: string]: string[] };
  } = { automations: {}, messages: {}, tasks: {} };

  hierarchy.forEach((x) => {
    x.effects.forEach((item) => {
      if (item.itemType === 'AUTOMATION') {
        const aC = (res.automations[item.item.id] =
          res.automations[item.item.id] || []);
        aC.push(x.cause.id);
      }
      if (item.itemType === 'MSG') {
        const mC = (res.messages[item.item.id] =
          res.messages[item.item.id] || []);
        mC.push(x.cause.id);
      }
      if (item.itemType === 'TASK') {
        const tC = (res.tasks[item.item.id] = res.tasks[item.item.id] || []);
        tC.push(x.cause.id);
      }
    });
  });
  return res;
};

export const getDirectEffectChildren = (d: Doc<IExperienceTemplate>) => {
  const hierarchy = getTriggerHierarchy(d);
  return hierarchy.reduce<{ [causeId: string]: string[] }>((m, x) => {
    m[x.cause.id] = x.effects.map((a) => a.item.id);
    return m;
  }, {});
};

export const getTriggerConnectors = (d: Doc<IExperienceTemplate>) => {
  const triggerHierarchy = getTriggerHierarchy(d);
  const triggerConnectors = triggerHierarchy.reduce<{
    [id: string]: {
      left?: 'up' | 'down' | 'upAndDown';
      right?: boolean;
    };
  }>((m, x) => {
    const containerCause = (m[x.cause.id] = m[x.cause.id] || {});
    containerCause.right = true;
    if (x.effects.length > 1) {
      x.effects.forEach((e, i) => {
        const containerEffect = (m[e.item.id] = m[e.item.id] || {});

        if (i === 0) {
          containerEffect.left = 'down';
        } else if (i === x.effects.length - 1) {
          containerEffect.left = 'up';
        } else {
          containerEffect.left = 'upAndDown';
        }
      });
    }
    return m;
  }, {});
  return triggerConnectors;
};

export const useTriggeredItemsForTask = (
  tpl: Doc<IExperienceTemplate>,
  taskTplId: string
): ITimelineItem[] => {
  return useMemo(() => getTriggeredItemsForTask(tpl, taskTplId), [
    tpl,
    taskTplId
  ]);
};

export const getResolvedTriggersForTimelineItem = (
  timing: IItemTemplateTiming,
  tasks: ITaskTemplate[]
): IItemTriggerResolved[] => {
  if (timing.type === 'triggered') {
    return compact(
      timing.dependentOn.map((d) => {
        if (d.itemType === 'TASK') {
          const task = tasks.find((t) => t.id === d.itemId);
          if (!task) {
            return null;
          }
          return {
            ...d,
            item: task
          };
        }
        return null;
      })
    );
  }
  return [];
};

export const getTplAdminTitle = (tpl: Doc<IExperienceTemplate>) =>
  tpl.data.adminTitle || tpl.data.title || '';
